/**
 * FavoritesDrawer Component
 * Slide-out drawer from right showing favorite products
 *
 * HDR-17: FavoritesDrawer feature for Core Frontend
 * Key differences from Storefront:
 * - NO "Add to cart" button (multi-merchant context)
 * - Shows merchant name as subtitle
 * - Uses useFavoriteProducts hook from HDR-14
 * - Navigates to /favorites (not /wishlist)
 */
import { Drawer, Stack, Title, ScrollArea, Button, Group } from '@mantine/core';
import { IconHeart } from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mantine/hooks';
import { useAuth } from '@teletienda/shared-auth-ui';
import { EmptyState, FavoritesDrawerItem } from '@teletienda/shared-ui-lib';
import { useUIStore } from '@/stores/uiStore';
import { useFavoriteProducts } from '@/hooks/useFavoriteProducts';
import { useCurrency } from '@/components/providers/CurrencyProvider';
import { getProductImageUrl } from '@/utils/images';

const MAX_ITEMS = 10;

export function FavoritesDrawer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 30em)');
  const { isAuthenticated } = useAuth();

  // UI state
  const { favoritesDrawerOpen, closeFavoritesDrawer } = useUIStore();

  // Currency conversion (HDR-18)
  const { userCurrency, getExchangeRate } = useCurrency();

  // Favorite products with catalog data
  const { availableProducts, isLoading, removeUnavailable } = useFavoriteProducts({
    limit: MAX_ITEMS,
  });

  const itemCount = availableProducts.length;

  const handleRemove = async (productGuid: string) => {
    await removeUnavailable(productGuid);
    // WebSocket sync happens automatically via useFavoritesSync
  };

  const handleGoToProducts = () => {
    closeFavoritesDrawer();
    navigate('/');
  };

  const handleLogin = () => {
    closeFavoritesDrawer();
    navigate('/login');
  };

  // Get merchant name from favorite metadata or fallback
  const getMerchantName = (merchantGuid: string, metadata?: Record<string, unknown>): string => {
    // Try metadata first (set when favorite was added from storefront)
    if (metadata?.merchant_name && typeof metadata.merchant_name === 'string') {
      return metadata.merchant_name;
    }
    // Fallback: truncated merchant GUID
    return `Tienda ${merchantGuid.substring(0, 8)}...`;
  };

  return (
    <Drawer
      opened={favoritesDrawerOpen}
      onClose={closeFavoritesDrawer}
      title={
        <Group gap="xs">
          <IconHeart size={20} />
          <Title order={4}>
            {t('favorites.title')} ({itemCount})
          </Title>
        </Group>
      }
      position="right"
      size={isMobile ? '100%' : 'md'}
      padding="md"
      data-testid="favorites-drawer"
    >
      {!isAuthenticated || itemCount === 0 ? (
        <EmptyState
          icon={<IconHeart size={64} stroke={1.5} />}
          title={t('favorites_drawer.empty')}
          description={
            isAuthenticated
              ? t('favorites_drawer.empty_description')
              : t('favorites_drawer.login_prompt')
          }
          action={{
            label: isAuthenticated
              ? t('favorites_drawer.explore')
              : t('login'),
            onClick: isAuthenticated ? handleGoToProducts : handleLogin,
          }}
        />
      ) : (
        <Stack h="100%" justify="space-between">
          <ScrollArea h="calc(100vh - 200px)" type="auto">
            <Stack gap="md">
              {isLoading ? (
                // Loading skeleton
                Array.from({ length: Math.min(3, MAX_ITEMS) }).map((_, i) => (
                  <FavoritesDrawerItem
                    key={`skeleton-${i}`}
                    image=""
                    name="Loading..."
                    price={0}
                    currency="ARS"
                    productUrl="#"
                    onRemove={() => {}}
                    isLoading
                    testId={`favorites-drawer-item-skeleton-${i}`}
                  />
                ))
              ) : (
                availableProducts.map(({ favorite, product, merchantName }) => {
                  if (!product) return null;
                  const productCurrency = product.currency || 'ARS';
                  return (
                    <FavoritesDrawerItem
                      key={product.product_guid}
                      image={getProductImageUrl(product.primary_image_url) || getProductImageUrl(product.images[0]?.url) || ''}
                      name={product.name}
                      subtitle={merchantName || getMerchantName(product.merchant_guid, favorite.metadata)}
                      price={product.price}
                      currency={productCurrency}
                      displayCurrency={userCurrency}
                      exchangeRate={getExchangeRate(productCurrency) ?? undefined}
                      productUrl={`/search?product=${product.product_guid}`}
                      onRemove={() => handleRemove(product.product_guid)}
                      testId={`favorites-drawer-item-${product.product_guid}`}
                    />
                  );
                })
              )}
            </Stack>
          </ScrollArea>

          <Stack gap="md">
            <Button
              component={Link}
              to="/favorites"
              variant="subtle"
              fullWidth
              onClick={closeFavoritesDrawer}
              data-testid="favorites-drawer-view-all"
            >
              {t('favorites_drawer.view_all')}
            </Button>
          </Stack>
        </Stack>
      )}
    </Drawer>
  );
}

export default FavoritesDrawer;
